import { TAppDispatch, TRootState } from 'store/types'
import slice from 'store/features/aiAvatar/slice'
import { captureException } from '@sentry/react'
import { ErrorKeys } from 'consts/errorKeys'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { handleApiError } from 'store/utils'
import { generateAvatar_API } from 'api'
import { aiAvatarSelectors } from 'store/features/aiAvatar/selectors'
import { userSelectors } from 'store/features/user/selectors'
import { withAppThunks } from 'store/with-app-thunks'
import { projectDataSelectors } from 'store/features/projectData/selectors'
import {
  buildAvatarPlaceholderMediaItem,
  clampQualityForFreeUser,
  resolveAvatarAspectRatio,
  resolveAvatarMediaDimensions
} from 'store/features/aiAvatar/utils'
import { resolveCompletedMediaRect } from 'utils/media'
import { IResolvedVisualMediaPlacement } from 'utils/visualMediaPlacement'
import { generationProgressService } from 'services/generationProgress'
import { PROJECT_MEDIA_TYPES } from 'consts'
import { MediaStatus } from 'types/media'
import { IAvatarGenerationResponse } from 'store/types/aiAvatar'

const { actions } = slice

// Avatar generation is a long multi-stage job (TTS + talking-head render), so
// we drive the timeline loader with the eased/fake-acceleration curve (like
// aiVideo) rather than the linear one used for images. The real completion
// (socket FINISHED) snaps it to 100% via `generationProgressService.complete`.
const EXPECTED_GENERATION_DURATION_SECONDS = 90

// Builds the request body from the current generation params and fires the
// generate-avatar API. Shared by the fresh-generate and regenerate flows so the
// body shape stays in sync. Callers validate `avatar.url` beforehand.
const requestAvatarGeneration = () =>
  withAppThunks(
    appThunks =>
      async (
        dispatch: TAppDispatch,
        getState: () => TRootState
      ): Promise<IAvatarGenerationResponse> => {
        const state = getState()
        const { quality, speechText, voice, avatar, aspectRatio } =
          aiAvatarSelectors.generationParams(state)
        const { aspectRatioString } =
          projectDataSelectors.defaultDimensions(state)
        // The avatar's own aspect ratio when the user picked one in the public
        // avatars panel, otherwise the project's (resolveAvatarAspectRatio).
        const effectiveAspectRatio = resolveAvatarAspectRatio(
          aspectRatio,
          aspectRatioString
        )

        // Enforce the premium-quality gate at the generate boundary, not just in
        // the dropdown UI: a free user can otherwise reach here with a premium
        // quality (e.g. regenerating a clip rendered while subscribed). Clamp to
        // a supported non-premium value before sending.
        const safeQuality = clampQualityForFreeUser(
          quality,
          aiAvatarSelectors.avatarPossibleQualities(state),
          userSelectors.isFreeUser(state)
        )

        const projectId = await dispatch(
          appThunks.projectThunks.getOrCreateProjectId()
        )

        const body = {
          quality: safeQuality,
          text: speechText,
          aspectRatio: effectiveAspectRatio,
          imageUrl: avatar?.url ?? '',
          speakerId: voice.speakerId,
          source: 'ai',
          sourceProjectId: projectId
        }

        const response = await generateAvatar_API(body)
        return response.data
      }
  )

// Fresh generation: insert a transient `aiAvatar` placeholder at the playhead
// and let the socket completion swap it for the rendered samples.
const generateAvatar = (placement?: IResolvedVisualMediaPlacement) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: () => TRootState) => {
      const { avatar, speechText, aspectRatio } =
        aiAvatarSelectors.generationParams(getState())
      const defaultDimensions =
        projectDataSelectors.defaultDimensions(getState())
      // Same resolution the request body uses (B2): the per-avatar ratio when
      // set, else the project ratio. The placeholder is FIT to this so the
      // loading clip matches the rendered samples' shape.
      const effectiveAspectRatio = resolveAvatarAspectRatio(
        aspectRatio,
        defaultDimensions.aspectRatioString
      )

      if (!avatar?.url) return

      dispatch(actions.setIsGenerating(true))
      try {
        const { aiAvatarVideoHash, aiAvatarProjectId } = (await dispatch(
          requestAvatarGeneration()
        )) as IAvatarGenerationResponse

        // A free user's gift/credit is consumed mid-render (at the voiceover
        // step), but gift access can't be re-checked accurately until then — so
        // hold the footer in a loading state from now until the
        // `voiceover_generated` socket event re-checks it (FINISHED/ERROR clear
        // it as a fallback). Free users only; a paid user's footer is unaffected.
        if (userSelectors.isFreeUser(getState())) {
          dispatch(actions.setAwaitingGiftRecheck(true))
        }

        const placeholder = buildAvatarPlaceholderMediaItem({
          id: aiAvatarVideoHash,
          assetId: String(aiAvatarProjectId),
          defaultDimensions,
          mediaItems: projectDataSelectors.mediaItems(getState()),
          placement,
          previewImageUrl: avatar.url,
          speechText,
          mediaDimensions: resolveAvatarMediaDimensions(
            avatar,
            effectiveAspectRatio
          )
        })

        // Start the simulated progress loader keyed by the placeholder
        // (id = hash, assetId = projectId) so the timeline/canvas ring climbs
        // while generation runs. The socket FINISHED/ERROR handlers tear it
        // down via complete()/stop().
        generationProgressService.start(
          placeholder.id,
          placeholder.assetId,
          EXPECTED_GENERATION_DURATION_SECONDS,
          true,
          0
        )

        dispatch(
          appThunks.projectDataThunks.addMediaItems({
            value: [placeholder]
          })
        )
      } catch (error) {
        if (!(error instanceof ApiError)) {
          captureException(new ReduxThunkError(error))
        }
        dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
        // Defensive: if a throw lands after the awaiting flag was set, release
        // the footer loading state so it can't get stuck (no generation = no
        // `voiceover_generated`/FINISHED to clear it).
        dispatch(actions.setAwaitingGiftRecheck(false))
      } finally {
        dispatch(actions.setIsGenerating(false))
      }
    }
  )

// Regeneration: the selected avatar clip already exists with real data, so we
// don't create a throwaway placeholder. Instead we re-key that clip to the new
// generation and flip it to PENDING — the socket completion (or, after a
// reload, `syncPendingAvatarData`) then replaces THIS clip in place. We can't
// change the clip's `id` (it's referenced across selection/loader/
// processing-items/undo history), so we re-key via `assetId`, storing the
// generation's `aiAvatarProjectId` there. That makes the clip correlatable both
// live — the completion handler matches `assetId === projectId` — and on
// reload, where `syncPendingAvatarData` re-queries the backend by that
// projectId. Trade-off vs. keying by the video hash: live correlation now leans
// on the stored projectId matching the socket event's projectId.
const regenerateAvatar = (mediaItemId: string) =>
  withAppThunks(
    appThunks => async (dispatch: TAppDispatch, getState: () => TRootState) => {
      const state = getState()
      const { avatar, aspectRatio } = aiAvatarSelectors.generationParams(state)
      const defaultDimensions = projectDataSelectors.defaultDimensions(state)

      if (!avatar?.url) return

      const targetItem = projectDataSelectors
        .mediaItems(state)
        .find(
          item =>
            item.id === mediaItemId &&
            item.type === PROJECT_MEDIA_TYPES.aiAvatar
        )
      if (!targetItem) return

      // Re-fit the clip's loading rect to the new aspect ratio so the loader
      // shows the right shape during regeneration. `resolveCompletedMediaRect`
      // keeps the user's current resize/reposition when the ratio is unchanged,
      // and FIT-recenters when it changed — the same rule the completion uses
      // (`positionSamplesAtPlaceholder`), so the loading box matches the final
      // clip with no jump.
      const effectiveAspectRatio = resolveAvatarAspectRatio(
        aspectRatio,
        defaultDimensions.aspectRatioString
      )
      const loadingRect = resolveCompletedMediaRect({
        currentWidth: targetItem.width,
        currentHeight: targetItem.height,
        currentCoordinates: targetItem.coordinates,
        newOriginal: resolveAvatarMediaDimensions(avatar, effectiveAspectRatio),
        canvasDimensions: defaultDimensions,
        rotation: targetItem.rotation
      })

      dispatch(actions.setIsGenerating(true))
      try {
        const { aiAvatarProjectId } = (await dispatch(
          requestAvatarGeneration()
        )) as IAvatarGenerationResponse

        // Regeneration consumes the gift/credit too — hold the footer loading
        // until the mid-render `voiceover_generated` re-check (FINISHED/ERROR as
        // fallback). Free users only. See `generateAvatar` for the rationale.
        if (userSelectors.isFreeUser(getState())) {
          dispatch(actions.setAwaitingGiftRecheck(true))
        }

        // Snapshot the clip's current data so a failed regeneration restores it
        // instead of deleting the user's content (the socket failure handler
        // reads this back via `handleFailedGenerationForMediaItem`).
        dispatch(appThunks.projectDataThunks.saveProcessingItemData(targetItem))

        // Re-key the existing clip to the new generation, keyed by the projectId
        // so both the completion handler and the load-time sync match it
        // (`assetId === projectId`).
        generationProgressService.start(
          targetItem.id,
          String(aiAvatarProjectId),
          EXPECTED_GENERATION_DURATION_SECONDS,
          true,
          0
        )

        // Re-key the clip to the generation's projectId and flip it to PENDING
        // so the completion (live socket or reload sync) replaces it in place.
        dispatch(
          appThunks.projectDataThunks.updateMediaItem({
            id: targetItem.id,
            updates: {
              assetId: String(aiAvatarProjectId),
              width: loadingRect.width,
              height: loadingRect.height,
              coordinates: { x: loadingRect.x, y: loadingRect.y },
              meta: {
                ...targetItem.meta,
                path: avatar.url,
                status: MediaStatus.PENDING
              }
            }
          })
        )
      } catch (error) {
        if (!(error instanceof ApiError)) {
          captureException(new ReduxThunkError(error))
        }
        dispatch(handleApiError(ErrorKeys.GENERAL_API_ERROR))
        // Defensive: if a throw lands after the awaiting flag was set, release
        // the footer loading state so it can't get stuck (no generation = no
        // `voiceover_generated`/FINISHED to clear it).
        dispatch(actions.setAwaitingGiftRecheck(false))
      } finally {
        dispatch(actions.setIsGenerating(false))
      }
    }
  )

export const generateThunks = {
  generateAvatar,
  regenerateAvatar
}
